/**
 * Press page content — 100% fidelity to content/company/press.md
 * Cross-reference: content/company/press.md
 * 18 sections, all handcrafted from source.
 */

export const pressMeta = {
  title: "Press & Media | Perception X2 News & Coverage",
  description:
    "Perception X2 press releases, media coverage, and industry recognition. Read the latest news from the world's most advanced autonomous perception amplification platform.",
  slug: "press",
  canonical: "https://perception.ac/press",
};

export interface HeroData {
  eyebrow: string;
  title: string;
  titleAccent: string;
  lede: string;
  body: string;
  cta: { label: string; href: string }[];
  trustIndicators: string[];
  pillars?: { title: string; body: string }[];
  intro?: string;
}

export const hero: HeroData = {
  eyebrow: "Press & Media",
  title: "Press & Media.",
  titleAccent: "Perception X2 in the News.",
  lede: "Perception X2 is redefining the boundaries of autonomous perception amplification. Explore our latest press releases, media coverage, industry recognition, and thought leadership insights.",
  body: "As the world's most advanced autonomous perception amplification platform, Perception X2 regularly appears in leading technology publications, industry analyst reports, and global media outlets. Our innovations in AI-powered narrative engineering and 144-dimensional emotional resonance mapping continue to shape the conversation around digital perception management.",
  cta: [
    { label: "Read Latest News", href: "#latest-news" },
    { label: "View Press Releases", href: "#press-releases" },
  ],
  trustIndicators: [
    "15+ Years of Industry Presence",
    "Featured in Leading Technology Publications",
    "Recognized by Global Industry Analysts",
    "300+ Enterprise and Government Clients",
  ],
};

export interface ExecSummaryData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const executiveSummary: ExecSummaryData = {
  eyebrow: "Executive Summary",
  title: "Perception X2: A Track Record of Innovation and Impact",
  intro: "For over 15 years, Perception X2 has operated at the forefront of autonomous perception amplification. Our journey from pioneering AI-driven reputation management to deploying the world's most comprehensive perception platform has been covered by technology journalists, industry analysts, and business media across multiple continents.",
  pillars: [
    {
      title: "Media Presence",
      body: "Perception X2 has been featured in publications spanning technology, business, government affairs, and digital strategy. Our platform's capabilities — including nine proprietary AI engines, 144-dimensional emotional resonance mapping, and 300-500% narrative penetration improvement — have attracted consistent editorial interest from leading outlets.",
    },
    {
      title: "Industry Analyst Coverage",
      body: "Global technology research firms have recognized Perception X2 as a leader in autonomous perception amplification. Our platform appears in analyst reports covering AI-powered reputation management, digital intelligence platforms, and enterprise narrative engineering solutions.",
    },
    {
      title: "Speaking Engagements",
      body: "Perception X2 executives are regularly invited to present at global technology conferences, government summits, and enterprise leadership forums. Our insights on autonomous perception amplification, AI-driven content intelligence, and digital landscape evolution are sought by event organizers worldwide.",
    },
  ],
  cta: [
    { label: "Explore Our Coverage", href: "#media-coverage" },
    { label: "Meet Our Team", href: "#executive-profile" },
  ],
};

export interface LatestNewsData {
  eyebrow: string;
  title: string;
  intro: string;
  headlines: { date: string; headline: string; category: string }[];
  milestones: { quarter: string; description: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const latestNews: LatestNewsData = {
  eyebrow: "Latest News",
  title: "Stay Current with Perception X2",
  intro: "Our latest developments, partnerships, and platform innovations are covered here first. From AI engine upgrades to global expansion milestones, Perception X2 continues to push the boundaries of what autonomous perception amplification can achieve.",
  headlines: [
    { date: "2026-06-10", headline: "Perception X2 Deploys Enhanced Narrative Intelligence Engine", category: "Platform Update" },
    { date: "2026-05-28", headline: "Autonomous Perception Amplification Adoption Grows 40% Year-Over-Year", category: "Industry Trend" },
    { date: "2026-05-15", headline: "Perception X2 Recognized as Leader in AI-Powered Perception Management", category: "Recognition" },
    { date: "2026-04-30", headline: "Global Enterprise Demand for Perception Engineering Surges", category: "Market Report" },
    { date: "2026-04-18", headline: "Perception X2 Expands Operations Across Additional Continents", category: "Company News" },
    { date: "2026-03-25", headline: "144-Dimensional Emotional Resonance Mapping Advances to Next Generation", category: "Technology" },
    { date: "2026-03-12", headline: "Perception X2 Achieves Record Client Retention Rate", category: "Business" },
  ],
  milestones: [
    { quarter: "Q2 2026", description: "Enhanced Narrative Intelligence Engine deployed across all tiers" },
    { quarter: "Q1 2026", description: "Client portfolio expands to 300+ global organizations" },
    { quarter: "Q4 2025", description: "144-dimensional emotional resonance mapping upgraded to v3.0" },
    { quarter: "Q3 2025", description: "Platform uptime maintained at 99.9999% for third consecutive year" },
    { quarter: "Q2 2025", description: "Autonomous operations engine processes 1 billion+ content pieces monthly" },
  ],
  cta: [
    { label: "View All News", href: "#latest-news" },
    { label: "Subscribe to Updates", href: "#press-contact" },
  ],
};

export interface ReleasesData {
  eyebrow: string;
  title: string;
  intro: string;
  year2026: { date: string; title: string; topic: string }[];
  year2025: { date: string; title: string; topic: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const pressReleases: ReleasesData = {
  eyebrow: "Press Releases",
  title: "Official Perception X2 Communications",
  intro: "Our press releases provide official statements on platform developments, partnerships, recognition, and strategic milestones. Media professionals can access full press materials through our media center.",
  year2026: [
    { date: "2026-06-10", title: "Perception X2 Launches Enhanced Narrative Intelligence Engine", topic: "Product" },
    { date: "2026-05-28", title: "Autonomous Perception Amplification Market Reaches New Heights", topic: "Industry" },
    { date: "2026-05-15", title: "Perception X2 Named Leader in AI-Powered Perception Management", topic: "Recognition" },
    { date: "2026-04-30", title: "Enterprise Demand for Narrative Engineering Platforms Surges Globally", topic: "Market" },
    { date: "2026-04-18", title: "Perception X2 Expands Multi-Continental Operations", topic: "Expansion" },
    { date: "2026-03-25", title: "Next-Generation Emotional Resonance Mapping Engine Released", topic: "Technology" },
    { date: "2026-03-12", title: "Perception X2 Reports Record Client Retention and Satisfaction", topic: "Business" },
    { date: "2026-02-20", title: "Nine Proprietary AI Engines Reach New Performance Benchmarks", topic: "Technology" },
    { date: "2026-01-30", title: "Perception X2 Annual Platform Performance Report Released", topic: "Report" },
    { date: "2026-01-15", title: "Global Perception Management Spending Projected to Double by 2028", topic: "Industry" },
  ],
  year2025: [
    { date: "2025-12-18", title: "Perception X2 Achieves Zero Security Incidents for 15th Consecutive Year", topic: "Security" },
    { date: "2025-11-05", title: "Autonomous Operations Engine Processes Record Volume", topic: "Technology" },
    { date: "2025-10-12", title: "Perception X2 Platform Uptime Exceeds 99.9999% Annual Target", topic: "Performance" },
    { date: "2025-09-20", title: "Enterprise Perception Management: State of the Industry Report", topic: "Report" },
    { date: "2025-08-08", title: "Perception X2 Expands Deployment Tier Capabilities", topic: "Product" },
    { date: "2025-07-15", title: "Global Client Portfolio Surpasses 250 Organizations", topic: "Business" },
    { date: "2025-06-22", title: "AI-Driven Narrative Engineering: Perception X2 Research Findings", topic: "Research" },
    { date: "2025-05-10", title: "Perception X2 Named Top Platform in Independent Evaluation", topic: "Recognition" },
    { date: "2025-04-05", title: "50+ Platform Coverage Achieved Across Digital Ecosystem", topic: "Capability" },
    { date: "2025-03-01", title: "Perception X2 Annual Client Satisfaction Survey Results Released", topic: "Business" },
  ],
  cta: [
    { label: "Access Full Press Center", href: "#press-releases" },
    { label: "Download Press Materials", href: "#media-kit" },
  ],
};

export interface CoverageData {
  eyebrow: string;
  title: string;
  intro: string;
  technology: { publication: string; article: string; date: string }[];
  business: { publication: string; article: string; date: string }[];
  government: { publication: string; article: string; date: string }[];
  analyst: { firm: string; report: string; date: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const mediaCoverage: CoverageData = {
  eyebrow: "Media Coverage",
  title: "Featured in Leading Publications",
  intro: "Perception X2 has been covered by technology journalists, business analysts, and industry publications across multiple continents. Our platform's innovations in autonomous perception amplification consistently generate editorial interest from authoritative media outlets.",
  technology: [
    { publication: "Technology Strategy Review", article: "The Rise of Autonomous Perception Amplification", date: "2026-05-20" },
    { publication: "Digital Intelligence Weekly", article: "How AI is Transforming Reputation Management", date: "2026-04-15" },
    { publication: "Enterprise Tech Today", article: "Perception X2: Inside the Platform Reshaping Digital Strategy", date: "2026-03-28" },
    { publication: "AI & Business Magazine", article: "144-Dimensional Emotional Mapping: A New Frontier", date: "2026-02-14" },
    { publication: "Global Tech Insights", article: "The Companies Leading the Perception Management Revolution", date: "2026-01-22" },
  ],
  business: [
    { publication: "Strategic Leadership Review", article: "Why Perception Management Is Now a C-Suite Priority", date: "2026-06-01" },
    { publication: "Enterprise Strategy Quarterly", article: "ROI of Autonomous Perception Amplification", date: "2026-05-10" },
    { publication: "Global Business Monitor", article: "The $50B Perception Management Market", date: "2026-04-01" },
    { publication: "Digital Transformation Report", article: "From Reactive to Proactive: The Perception Shift", date: "2026-03-05" },
    { publication: "Future of Enterprise", article: "AI-Powered Narrative Engineering at Scale", date: "2026-02-18" },
  ],
  government: [
    { publication: "Public Affairs Technology", article: "Sovereign Perception Management in the Digital Age", date: "2026-05-22" },
    { publication: "Government Digital Strategy", article: "How Public Sector Organizations Manage Digital Perception", date: "2026-04-10" },
    { publication: "Civic Technology Review", article: "AI and Public Trust: Opportunities and Challenges", date: "2026-03-15" },
    { publication: "Policy Technology Monitor", article: "Strategic Communication in the Era of Autonomous Systems", date: "2026-02-08" },
    { publication: "Global Governance Tech", article: "Narrative Management for Sovereign Entities", date: "2026-01-12" },
  ],
  analyst: [
    { firm: "Digital Perception Research Group", report: "Leader in Autonomous Perception Amplification", date: "2026-05-15" },
    { firm: "AI Platform Analytics", report: "Perception X2: Technology Deep Dive", date: "2026-04-20" },
    { firm: "Enterprise Intelligence Quarterly", report: "Magic Quadrant for Perception Management Platforms", date: "2026-03-10" },
    { firm: "Global Technology Advisors", report: "Market Guide for AI-Powered Reputation Platforms", date: "2026-02-25" },
    { firm: "Strategic Technology Insights", report: "Innovation Radar: Perception X2 Platform", date: "2026-01-30" },
  ],
  cta: [
    { label: "View All Coverage", href: "#media-coverage" },
    { label: "Request Media Access", href: "#press-contact" },
  ],
};

export interface RecognitionData {
  eyebrow: string;
  title: string;
  intro: string;
  analyst: { firm: string; recognition: string; year: string }[];
  technology: { award: string; category: string; year: string }[];
  business: { recognition: string; category: string; year: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const industryRecognition: RecognitionData = {
  eyebrow: "Industry Recognition",
  title: "Acknowledged by Leaders in Technology and Business",
  intro: "Perception X2 has received consistent recognition from industry analysts, technology reviewers, and business publications. Our platform's performance metrics, security record, and innovation trajectory have been evaluated by independent organizations.",
  analyst: [
    { firm: "Digital Perception Research Group", recognition: "Leader in Autonomous Perception Amplification", year: "2026" },
    { firm: "AI Platform Analytics", recognition: "Top-Rated AI Perception Platform", year: "2026" },
    { firm: "Enterprise Intelligence Quarterly", recognition: "Leader Position — Perception Management", year: "2025" },
    { firm: "Global Technology Advisors", recognition: "Representative Vendor — AI Reputation Platforms", year: "2025" },
    { firm: "Strategic Technology Insights", recognition: "Innovation Leader — Narrative Engineering", year: "2025" },
  ],
  technology: [
    { award: "AI Platform Excellence Award", category: "Best Autonomous Perception System", year: "2026" },
    { award: "Enterprise Technology Innovation Award", category: "Most Advanced Narrative Engineering Platform", year: "2026" },
    { award: "Digital Strategy Leadership Award", category: "Best AI-Powered Perception Management", year: "2025" },
    { award: "Security Excellence Award", category: "Zero Security Incidents — 15 Years", year: "2025" },
    { award: "Platform Performance Award", category: "Highest Uptime in Industry (99.9999%)", year: "2025" },
  ],
  business: [
    { recognition: "Fastest Growing Perception Platform", category: "Enterprise Adoption Rate", year: "2026" },
    { recognition: "Client Satisfaction Excellence", category: "Highest Retention in Sector", year: "2026" },
    { recognition: "Innovation in AI-Driven Perception", category: "Technology Breakthrough", year: "2025" },
    { recognition: "Global Expansion Recognition", category: "Multi-Continental Operations", year: "2025" },
    { recognition: "Enterprise Platform of the Year", category: "Overall Platform Excellence", year: "2024" },
  ],
  cta: [
    { label: "View All Recognition", href: "#industry-recognition" },
    { label: "Read About Our Platform", href: "#latest-news" },
  ],
};

export interface AnalystCoverageData {
  eyebrow: string;
  title: string;
  intro: string;
  reports: { firm: string; report: string; coverage: string }[];
  commentary: { source: string; quote: string; year: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const analystCoverage: AnalystCoverageData = {
  eyebrow: "Analyst Coverage",
  title: "Featured in Leading Technology Research",
  intro: "Global technology research firms regularly evaluate Perception X2 within their coverage of AI-powered perception management, digital intelligence platforms, and enterprise narrative engineering solutions. Our platform's performance, innovation, and market position are documented across multiple analyst reports.",
  reports: [
    { firm: "Digital Perception Research Group", report: "Autonomous Perception Amplification Market Landscape 2026", coverage: "Leader" },
    { firm: "AI Platform Analytics", report: "AI-Powered Reputation Management: Vendor Assessment 2026", coverage: "Top Rated" },
    { firm: "Enterprise Intelligence Quarterly", report: "Magic Quadrant for Perception Management Platforms 2025", coverage: "Leader" },
    { firm: "Global Technology Advisors", report: "Market Guide for AI Reputation Platforms 2025", coverage: "Representative Vendor" },
    { firm: "Strategic Technology Insights", report: "Innovation Radar: Perception Engineering Platforms 2025", coverage: "Innovation Leader" },
    { firm: "Digital Strategy Research", report: "Enterprise Perception Management Buyer's Guide 2024", coverage: "Recommended" },
    { firm: "AI Analytics Group", report: "Narrative Engineering Technology Landscape 2024", coverage: "Pioneer" },
  ],
  commentary: [
    { source: "Digital Perception Research Group, 2026", quote: "Perception X2's nine proprietary AI engines represent a significant technical advantage in the autonomous perception amplification market.", year: "2026" },
    { source: "AI Platform Analytics, 2026", quote: "The platform's 144-dimensional emotional resonance mapping is a unique analytical capability that no other vendor currently matches.", year: "2026" },
    { source: "Enterprise Intelligence Quarterly, 2025", quote: "With 99.9999% uptime and zero security incidents over 15 years, Perception X2 demonstrates operational excellence that sets the standard for enterprise-grade perception management.", year: "2025" },
    { source: "Global Technology Advisors, 2025", quote: "Organizations seeking autonomous, AI-powered perception amplification at scale should evaluate Perception X2 as a leading option.", year: "2025" },
  ],
  cta: [
    { label: "Request Analyst Reports", href: "#press-contact" },
    { label: "Explore Our Technology", href: "#latest-news" },
  ],
};

export interface ThoughtLeadershipData {
  eyebrow: string;
  title: string;
  intro: string;
  perspectives: { topic: string; publication: string; date: string }[];
  themes: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const thoughtLeadership: ThoughtLeadershipData = {
  eyebrow: "Thought Leadership",
  title: "Insights from the Frontlines of Perception Engineering",
  intro: "Perception X2 leadership contributes regularly to the discourse on autonomous perception amplification, AI-driven narrative engineering, and digital landscape evolution. Our perspectives are shaped by 15+ years of operational experience across multiple continents.",
  perspectives: [
    { topic: "The Future of Autonomous Perception Amplification", publication: "Technology Strategy Review", date: "2026-05" },
    { topic: "Why Narrative Engineering Is the Next Enterprise Imperative", publication: "Strategic Leadership Review", date: "2026-04" },
    { topic: "144-Dimensional Emotional Mapping: Beyond Traditional Sentiment Analysis", publication: "AI & Business Magazine", date: "2026-03" },
    { topic: "From Reputation Management to Perception Engineering: The Evolution", publication: "Enterprise Strategy Quarterly", date: "2026-02" },
    { topic: "The Role of AI in Shaping Public Perception", publication: "Public Affairs Technology", date: "2026-01" },
    { topic: "Operational Excellence at Scale: Lessons from 15 Years of Global Operations", publication: "Digital Intelligence Weekly", date: "2025-12" },
    { topic: "Why Organic Authenticity Matters in AI-Generated Content", publication: "AI Analytics Group", date: "2025-11" },
    { topic: "Sovereign Perception Management: A Strategic Imperative", publication: "Government Digital Strategy", date: "2025-10" },
  ],
  themes: [
    "Autonomous Perception Amplification — The shift from manual reputation management to AI-driven perception engineering",
    "Organic Authenticity — Why 95-98% organic authenticity rating matters for enterprise perception strategies",
    "Emotional Resonance Mapping — How 144-dimensional analysis transforms audience engagement",
    "Crisis Preparedness — Proactive versus reactive approaches to perception management",
    "Platform Proliferation — Managing narrative consistency across 50+ digital platforms",
    "Sovereign Perception — Strategic communication for government and public sector entities",
  ],
  cta: [
    { label: "Read Our Insights", href: "#thought-leadership" },
    { label: "Request Expert Commentary", href: "#press-contact" },
  ],
};

export interface EventsData {
  eyebrow: string;
  title: string;
  intro: string;
  upcoming: { event: string; location: string; date: string; topic: string }[];
  past: { event: string; year: string; topic: string; attendance: string }[];
  topics: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const conferencesEvents: EventsData = {
  eyebrow: "Conferences & Events",
  title: "Leading the Conversation on Autonomous Perception",
  intro: "Perception X2 executives are regularly invited to present at global technology conferences, government summits, enterprise leadership forums, and industry events. Our expertise in autonomous perception amplification is sought by event organizers worldwide.",
  upcoming: [
    { event: "Global AI Summit 2026", location: "Geneva, Switzerland", date: "2026-07-15", topic: "Autonomous Perception Amplification: The Next Frontier" },
    { event: "Enterprise Technology Conference", location: "Singapore", date: "2026-08-20", topic: "AI-Driven Narrative Engineering at Scale" },
    { event: "Government Digital Strategy Forum", location: "Washington, D.C.", date: "2026-09-10", topic: "Sovereign Perception Management" },
    { event: "Perception Management Industry Summit", location: "London, UK", date: "2026-10-05", topic: "144-Dimensional Emotional Mapping: Beyond Sentiment" },
    { event: "AI & Enterprise Leadership Conference", location: "Dubai, UAE", date: "2026-11-12", topic: "The Future of Autonomous Perception Systems" },
    { event: "Digital Strategy Executive Forum", location: "Tokyo, Japan", date: "2026-12-03", topic: "Multi-Platform Narrative Engineering" },
  ],
  past: [
    { event: "Global AI Summit", year: "2025", topic: "Autonomous Perception Amplification at Enterprise Scale", attendance: "3,500+" },
    { event: "Enterprise Technology Conference", year: "2025", topic: "Nine AI Engines: The Architecture of Perception Dominance", attendance: "2,800+" },
    { event: "Government Digital Strategy Forum", year: "2025", topic: "Public Sector Perception Management", attendance: "1,200+" },
    { event: "Perception Management Summit", year: "2025", topic: "Organic Authenticity in AI Content", attendance: "1,500+" },
    { event: "AI & Enterprise Forum", year: "2024", topic: "From Reactive to Autonomous: The Perception Shift", attendance: "2,200+" },
    { event: "Digital Strategy Conference", year: "2024", topic: "Scaling Narrative Engineering to 50+ Platforms", attendance: "1,800+" },
  ],
  topics: [
    "Autonomous Perception Amplification: Architecture and Outcomes",
    "144-Dimensional Emotional Resonance Mapping: Technical Deep Dive",
    "Organic Authenticity at Scale: How AI Content Passes Organic Detection",
    "Crisis Response: From Hours to Minutes with Autonomous Systems",
    "Sovereign Perception Management: Government Applications",
    "The Future of AI-Driven Narrative Engineering",
  ],
  cta: [
    { label: "View Event Calendar", href: "#conferences-events" },
    { label: "Invite a Speaker", href: "#press-contact" },
  ],
};

export interface MediaKitData {
  eyebrow: string;
  title: string;
  intro: string;
  resources: { resource: string; format: string; description: string }[];
  brand: { label: string; body: string }[];
  facts: { fact: string; value: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const mediaKit: MediaKitData = {
  eyebrow: "Media Kit",
  title: "Press Resources for Journalists and Analysts",
  intro: "Perception X2 provides comprehensive media resources for journalists, analysts, and content creators covering autonomous perception amplification and AI-powered reputation management.",
  resources: [
    { resource: "Perception X2 Brand Guidelines", format: "PDF", description: "Logo usage, color palette, typography, brand voice" },
    { resource: "Platform Overview", format: "PDF", description: "Comprehensive overview of nine AI engines and capabilities" },
    { resource: "Fact Sheet", format: "PDF", description: "Key statistics, metrics, and company facts" },
    { resource: "Executive Headshots", format: "JPEG", description: "High-resolution leadership team photographs" },
    { resource: "Platform Screenshots", format: "PNG", description: "Dashboard, analytics, and engine visualizations" },
    { resource: "Whitepapers", format: "PDF", description: "Research papers on autonomous perception amplification" },
    { resource: "Annual Reports", format: "PDF", description: "Performance data, metrics, and operational highlights" },
    { resource: "Case Studies", format: "PDF", description: "Client success stories and outcomes" },
  ],
  brand: [
    { label: "Logo formats", body: "SVG, PNG, EPS" },
    { label: "Color palette", body: "Available in brand guidelines document" },
    { label: "Typography", body: "Specified in brand guidelines document" },
    { label: "Usage restrictions", body: "Editorial use only; modification not permitted" },
  ],
  facts: [
    { fact: "Company Name", value: "Perception X2 (operated by CryptoMize)" },
    { fact: "Founded", value: "2009" },
    { fact: "Headquarters", value: "Global operations across multiple continents" },
    { fact: "Platform Uptime", value: "99.9999%" },
    { fact: "Security Incidents", value: "Zero (15+ years)" },
    { fact: "AI Engines", value: "Nine proprietary platforms" },
    { fact: "Platform Coverage", value: "50+ digital platforms" },
    { fact: "Emotional Dimensions", value: "144-dimensional mapping" },
    { fact: "Narrative Improvement", value: "300-500% over organic baseline" },
    { fact: "Organic Authenticity", value: "95-98%" },
    { fact: "Maximum Persona Scale", value: "1,000,000+" },
    { fact: "Client Base", value: "300+ global organizations" },
  ],
  cta: [
    { label: "Download Media Kit", href: "#media-kit" },
    { label: "Contact Press Team", href: "#press-contact" },
  ],
};

export interface ExecProfileData {
  eyebrow: string;
  title: string;
  intro: string;
  leadership: { name: string; role: string; expertise: string }[];
  availability: string[];
  expertise: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const executiveProfile: ExecProfileData = {
  eyebrow: "Executive Profile",
  title: "Leadership Behind Perception X2",
  intro: "Perception X2 is led by a team of seasoned professionals with deep expertise in AI technology, perception management, and enterprise strategy. Our leadership team's insights are regularly sought by media, analysts, and conference organizers.",
  leadership: [
    { name: "Perception X2 Leadership", role: "Chief Executive Officer", expertise: "15+ years in autonomous perception amplification and AI platform development" },
    { name: "Perception X2 Leadership", role: "Chief Technology Officer", expertise: "Proprietary AI engine architecture, 144-dimensional emotional resonance mapping" },
    { name: "Perception X2 Leadership", role: "Chief Operating Officer", expertise: "Global operations, multi-continental deployment, enterprise client management" },
    { name: "Perception X2 Leadership", role: "Chief Strategy Officer", expertise: "Market positioning, competitive strategy, industry analysis" },
    { name: "Perception X2 Leadership", role: "Chief Security Officer", expertise: "Zero security incidents, military-grade security architecture, compliance" },
  ],
  availability: [
    "Media Interviews — Print, broadcast, and digital media interviews on topics related to autonomous perception amplification, AI-driven narrative engineering, and digital landscape trends",
    "Expert Commentary — Timely commentary on industry developments, technology trends, and market dynamics",
    "Conference Speaking — Keynote presentations, panel participation, and workshop facilitation",
    "Analyst Briefings — One-on-one briefings for technology analysts and researchers",
    "Bylined Articles — Guest articles and contributed content for industry publications",
  ],
  expertise: [
    "Autonomous perception amplification architecture and deployment",
    "AI-powered narrative engineering and content intelligence",
    "144-dimensional emotional resonance mapping technology",
    "Enterprise-grade security and zero-trust architecture",
    "Global operations across multiple continents and regulatory environments",
    "Crisis response systems and real-time perception management",
    "Organic authenticity measurement and optimization",
  ],
  cta: [
    { label: "Request Interview", href: "#press-contact" },
    { label: "View Full Team", href: "/leadership" },
  ],
};

export interface MilestonesData {
  eyebrow: string;
  title: string;
  intro: string;
  timeline: { year: string; milestone: string }[];
  growth: { metric: string; years: { year: string; value: string }[] }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const companyMilestones: MilestonesData = {
  eyebrow: "Company Milestones",
  title: "15+ Years of Perception Innovation",
  intro: "Perception X2 has maintained a consistent trajectory of innovation, growth, and operational excellence since its founding. Our milestones reflect a sustained commitment to advancing autonomous perception amplification technology.",
  timeline: [
    { year: "2009", milestone: "Company founded; initial perception management platform launched" },
    { year: "2011", milestone: "First AI engine deployed for automated narrative analysis" },
    { year: "2013", milestone: "Expanded operations to multiple continents" },
    { year: "2015", milestone: "Achieved 99.9999% uptime SLA for the first time" },
    { year: "2017", milestone: "Launched 144-dimensional emotional resonance mapping (v1.0)" },
    { year: "2018", milestone: "Client portfolio surpasses 100 global organizations" },
    { year: "2019", milestone: "Nine proprietary AI engines fully operational" },
    { year: "2020", milestone: "Platform coverage expands to 50+ digital platforms" },
    { year: "2021", milestone: "Achieved zero security incidents milestone (12 years)" },
    { year: "2022", milestone: "Autonomous operations engine deployed at enterprise scale" },
    { year: "2023", milestone: "Client portfolio surpasses 200 global organizations" },
    { year: "2024", milestone: "144-dimensional emotional resonance mapping upgraded to v2.0" },
    { year: "2025", milestone: "300+ client organizations; 15th year with zero security incidents" },
    { year: "2026", milestone: "Enhanced Narrative Intelligence Engine deployed; market leadership consolidated" },
  ],
  growth: [
    { metric: "Client Organizations", years: [{ year: "2020", value: "120" }, { year: "2022", value: "180" }, { year: "2024", value: "260" }, { year: "2026", value: "300+" }] },
    { metric: "AI Engine Performance", years: [{ year: "2020", value: "Baseline" }, { year: "2022", value: "+40%" }, { year: "2024", value: "+85%" }, { year: "2026", value: "+120%" }] },
    { metric: "Platform Coverage", years: [{ year: "2020", value: "35 platforms" }, { year: "2022", value: "42 platforms" }, { year: "2024", value: "50+ platforms" }, { year: "2026", value: "50+ platforms" }] },
    { metric: "Narrative Penetration", years: [{ year: "2020", value: "200-350%" }, { year: "2022", value: "250-400%" }, { year: "2024", value: "300-500%" }, { year: "2026", value: "300-500%" }] },
    { metric: "Organic Authenticity", years: [{ year: "2020", value: "90-93%" }, { year: "2022", value: "92-95%" }, { year: "2024", value: "95-98%" }, { year: "2026", value: "95-98%" }] },
  ],
  cta: [
    { label: "Review Our History", href: "/history" },
    { label: "Explore Our Platform", href: "#latest-news" },
  ],
};

export interface AwardsData {
  eyebrow: string;
  title: string;
  intro: string;
  technology: { award: string; category: string; year: string; org: string }[];
  performance: { award: string; category: string; year: string; org: string }[];
  leadership: { award: string; category: string; year: string; org: string }[];
  research: { award: string; category: string; year: string; org: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const industryAwards: AwardsData = {
  eyebrow: "Industry Awards",
  title: "Recognized for Excellence and Innovation",
  intro: "Perception X2 has received numerous awards recognizing our platform's technology, performance, and impact on the autonomous perception amplification industry.",
  technology: [
    { award: "AI Platform Excellence Award", category: "Best Autonomous Perception System", year: "2026", org: "AI Technology Association" },
    { award: "Enterprise Innovation Award", category: "Most Advanced Narrative Engineering Platform", year: "2026", org: "Enterprise Technology Council" },
    { award: "Best AI Architecture", category: "Perception Management Engine Design", year: "2025", org: "Digital Innovation Institute" },
    { award: "Platform Engineering Excellence", category: "Nine AI Engine Architecture", year: "2025", org: "Technology Strategy Group" },
  ],
  performance: [
    { award: "Platform Performance Award", category: "Highest Uptime in Industry (99.9999%)", year: "2026", org: "Enterprise Performance Association" },
    { award: "Reliability Excellence Award", category: "15 Years Zero Security Incidents", year: "2025", org: "Security Industry Council" },
    { award: "Scalability Award", category: "1,000,000+ Persona Deployment Capability", year: "2025", org: "Cloud & Scale Institute" },
    { award: "Response Time Excellence", category: "Real-Time Crisis Response Performance", year: "2024", org: "Operational Excellence Group" },
  ],
  leadership: [
    { award: "Perception Management Platform of the Year", category: "Overall Platform Excellence", year: "2026", org: "Digital Strategy Awards" },
    { award: "Fastest Growing Platform", category: "Enterprise Adoption Rate", year: "2026", org: "Market Growth Institute" },
    { award: "Client Satisfaction Excellence", category: "Highest Retention in Sector", year: "2025", org: "Customer Experience Council" },
    { award: "Innovation in AI Perception", category: "Technology Breakthrough", year: "2025", org: "AI Innovation Network" },
    { award: "Global Expansion Recognition", category: "Multi-Continental Operations", year: "2024", org: "International Business Association" },
  ],
  research: [
    { award: "Emotional Resonance Innovation", category: "144-Dimensional Mapping Technology", year: "2026", org: "AI Research Council" },
    { award: "Content Intelligence Breakthrough", category: "Organic Authenticity Achievement", year: "2025", org: "Natural Language Processing Institute" },
    { award: "Autonomous Systems Excellence", category: "Self-Operating Perception Platform", year: "2025", org: "Autonomous Systems Society" },
    { award: "Data Analytics Innovation", category: "Real-Time Perception Analytics", year: "2024", org: "Data Science Association" },
  ],
  cta: [
    { label: "View All Awards", href: "#industry-awards" },
    { label: "Learn About Our Technology", href: "#latest-news" },
  ],
};

export interface PublicationsData {
  eyebrow: string;
  title: string;
  intro: string;
  research: { title: string; publication: string; date: string }[];
  whitepapers: { title: string; focus: string; date: string }[];
  citations: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const publicationsResearch: PublicationsData = {
  eyebrow: "Publications & Research",
  title: "Contributing to the Field of Autonomous Perception Amplification",
  intro: "Perception X2 contributes original research and insights to the growing body of knowledge on autonomous perception amplification, AI-driven narrative engineering, and emotional resonance mapping. Our publications are cited by researchers, analysts, and practitioners worldwide.",
  research: [
    { title: "144-Dimensional Emotional Resonance Mapping: A Framework for Precision Audience Analysis", publication: "AI Research Quarterly", date: "2026-03" },
    { title: "Organic Authenticity in AI-Generated Content: Measurement and Optimization", publication: "Natural Language Processing Journal", date: "2025-11" },
    { title: "Autonomous Narrative Amplification: Architecture and Outcomes at Scale", publication: "Digital Intelligence Review", date: "2025-08" },
    { title: "Multi-Platform Perception Management: Coordinated Deployment Across 50+ Surfaces", publication: "Enterprise Technology Journal", date: "2025-05" },
    { title: "Real-Time Crisis Response in Autonomous Perception Systems", publication: "Strategic Communication Review", date: "2025-02" },
    { title: "The Economics of AI-Driven Perception Management", publication: "Business Technology Quarterly", date: "2024-10" },
  ],
  whitepapers: [
    { title: "The Autonomous Perception Amplification Framework", focus: "Platform Architecture", date: "2026-01" },
    { title: "144-Dimensional Emotional Mapping: Technical Deep Dive", focus: "AI Technology", date: "2025-09" },
    { title: "Organic Authenticity: Why 95-98% Changes Everything", focus: "Content Intelligence", date: "2025-06" },
    { title: "Sovereign Perception Management: Government Applications", focus: "Public Sector", date: "2025-03" },
    { title: "Crisis Response at Machine Speed: Autonomous Systems in Action", focus: "Crisis Management", date: "2024-12" },
    { title: "The Future of Narrative Engineering: 2025 and Beyond", focus: "Industry Forecast", date: "2024-09" },
  ],
  citations: [
    "45+ academic papers on AI and perception management",
    "20+ industry analyst reports",
    "15+ government strategy documents",
    "10+ enterprise technology assessments",
    "30+ industry publication articles",
  ],
  cta: [
    { label: "Access Our Research", href: "#publications-research" },
    { label: "Request Research Papers", href: "#press-contact" },
  ],
};

export interface PodcastData {
  eyebrow: string;
  title: string;
  intro: string;
  appearances: { show: string; topic: string; format: string; date: string }[];
  topics: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const podcastInterviews: PodcastData = {
  eyebrow: "Podcast & Interviews",
  title: "Insights in Audio and Video Format",
  intro: "Perception X2 leadership regularly participates in podcast interviews, video discussions, and media appearances covering autonomous perception amplification, AI-driven narrative engineering, and digital landscape trends.",
  appearances: [
    { show: "The Future of AI — Technology Podcast", topic: "Autonomous Perception Amplification: The Next Frontier", format: "Podcast", date: "2026-06-01" },
    { show: "Enterprise Strategy Hour — Business Podcast", topic: "Why Perception Management Is Now a C-Suite Priority", format: "Podcast", date: "2026-05-15" },
    { show: "Digital Intelligence Review — Video Series", topic: "144-Dimensional Emotional Mapping Explained", format: "Video", date: "2026-04-28" },
    { show: "Government Tech Today — Policy Podcast", topic: "Sovereign Perception Management in Practice", format: "Podcast", date: "2026-04-10" },
    { show: "AI & Enterprise — Industry Podcast", topic: "Nine AI Engines: The Architecture of Perception Dominance", format: "Podcast", date: "2026-03-22" },
    { show: "The CTO Perspective — Technology Interview", topic: "Building 99.9999% Uptime at Global Scale", format: "Video", date: "2026-03-05" },
    { show: "Narrative Engineering Monthly — Industry Podcast", topic: "Organic Authenticity: How AI Content Passes Organic Detection", format: "Podcast", date: "2026-02-15" },
    { show: "Strategic Communication Forum — Panel Discussion", topic: "The Future of AI-Driven Perception Management", format: "Video", date: "2026-01-28" },
  ],
  topics: [
    "How autonomous perception amplification is transforming enterprise strategy",
    "The technical architecture behind nine proprietary AI engines",
    "Why 144-dimensional emotional resonance mapping outperforms traditional sentiment analysis",
    "Organic authenticity: What it means and why it matters at scale",
    "Lessons from 15+ years of global perception management operations",
    "Crisis response at machine speed: Autonomous systems in action",
    "Sovereign perception management: Government applications and considerations",
    "The economics of AI-driven perception amplification",
  ],
  cta: [
    { label: "Listen to Episodes", href: "#podcast-interviews" },
    { label: "Request an Interview", href: "#press-contact" },
  ],
};

export interface PressContactData {
  eyebrow: string;
  title: string;
  intro: string;
  contacts: { purpose: string; contact: string; response: string }[];
  resources: string[];
  embargo: { title: string; body: string };
  guidelines: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const pressContact: PressContactData = {
  eyebrow: "Press Contact",
  title: "Reach Our Media Team",
  intro: "Perception X2 welcomes inquiries from journalists, analysts, researchers, and content creators. Our press team provides timely responses to media requests, including interview coordination, fact verification, and resource distribution.",
  contacts: [
    { purpose: "General Media Inquiries", contact: "press@perception.ac", response: "Within 24 hours" },
    { purpose: "Interview Requests", contact: "press@perception.ac", response: "Within 48 hours" },
    { purpose: "Fact Verification", contact: "press@perception.ac", response: "Within 24 hours" },
    { purpose: "Media Kit Requests", contact: "press@perception.ac", response: "Within 24 hours" },
    { purpose: "Analyst Briefings", contact: "press@perception.ac", response: "Within 48 hours" },
    { purpose: "Expert Commentary", contact: "press@perception.ac", response: "Within 24 hours" },
  ],
  resources: [
    "Press Releases — Official Perception X2 communications and announcements",
    "Media Kit — Brand assets, fact sheets, executive headshots, and platform visualizations",
    "Research Papers — Published whitepapers and research publications",
    "Expert Sources — Executive team availability for interviews and commentary",
    "Fact Sheet — Key company statistics and platform metrics",
  ],
  embargo: {
    title: "Embargo and Off-Record Policies",
    body: "Embargoed materials are available upon request for qualifying publications. Off-record conversations can be arranged with advance coordination. All media interactions are handled by our dedicated press team. Confidential source protection is maintained per journalistic standards.",
  },
  guidelines: [
    "Response within 24 hours for standard inquiries",
    "Response within 48 hours for interview and briefing requests",
    "Fact verification provided same business day when possible",
    "High-resolution assets available for immediate download",
  ],
  cta: [
    { label: "Contact Press Team", href: "mailto:press@perception.ac" },
    { label: "Download Media Kit", href: "#media-kit" },
  ],
};

export interface CtaData {
  eyebrow: string;
  title: string;
  intro: string;
  audience: { title: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const pressCta: CtaData = {
  eyebrow: "Get Started",
  title: "Stay Informed About Perception X2",
  intro: "Autonomous perception amplification is reshaping how organizations manage their digital presence. Stay informed about Perception X2 platform developments, industry insights, and thought leadership through our media channels.",
  audience: [
    { title: "For Media Professionals", body: "Access press releases, media kit resources, executive interviews, and expert commentary. Our press team is available to support your coverage of autonomous perception amplification and AI-driven narrative engineering." },
    { title: "For Industry Analysts", body: "Request analyst briefings, platform demonstrations, and research papers. Perception X2 leadership is available for one-on-one briefings covering platform architecture, performance metrics, and market positioning." },
    { title: "For Conference Organizers", body: "Invite Perception X2 executives to present at your next event. Our speakers cover autonomous perception amplification, AI-driven content intelligence, emotional resonance mapping, and crisis response systems." },
    { title: "For Researchers", body: "Access published research, whitepapers, and data on autonomous perception amplification. Perception X2 contributes original research to the growing body of knowledge in AI-driven perception management." },
  ],
  cta: [
    { label: "Contact Press Team", href: "mailto:press@perception.ac" },
    { label: "Download Media Kit", href: "#media-kit" },
    { label: "Request Demo", href: "/demo" },
  ],
};

export interface AboutPxData {
  eyebrow: string;
  title: string;
  metrics: { metric: string; value: string }[];
  whatWeDo: string;
  whoWeServe: string;
  commitments: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
  intro?: string;
}

export const aboutPx: AboutPxData = {
  eyebrow: "About Perception X2",
  title: "The World's Most Advanced Autonomous Perception Amplification Platform",
  intro: "",
  metrics: [
    { metric: "Narrative Penetration Improvement", value: "300-500%" },
    { metric: "Organic Authenticity Rating", value: "95-98%" },
    { metric: "Platform Coverage", value: "50+ digital platforms" },
    { metric: "Emotional Resonance Dimensions", value: "144" },
    { metric: "Maximum Persona Deployment", value: "1,000,000+" },
    { metric: "Proprietary AI Engines", value: "9" },
    { metric: "Platform Uptime", value: "99.9999%" },
    { metric: "Security Incidents", value: "Zero (15+ years)" },
    { metric: "Client Organizations", value: "300+" },
    { metric: "Operational Regions", value: "Multiple continents" },
  ],
  whatWeDo: "Perception X2 transforms passive reputation management into active perception engineering. Our autonomous AI platform shapes, protects, and amplifies your digital presence across 50+ platforms, achieving results that traditional approaches cannot replicate.",
  whoWeServe: "Governments, Fortune 500 enterprises, political organizations, public figures, defense agencies, and financial institutions trust Perception X2 for autonomous perception amplification at global scale.",
  commitments: [
    "Excellence in every operation",
    "Innovation in every technology",
    "Security in every interaction",
    "Results in every engagement",
  ],
  cta: [
    { label: "Learn About Us", href: "/about" },
    { label: "Request a Demo", href: "/demo" },
    { label: "Contact Us", href: "/contact" },
  ],
};
